import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import {
  createQlikConnectionInputSchema,
  type CreateQlikConnectionInput,
  type QlikAuthMethod,
} from "@repo/shared";
import { useTranslation } from "react-i18next";
import { Button, PageContent, PageHeader, PageShell } from "@h2j-data-team/ui";

import { BackLink } from "@/components/back-link";
import { useCreateConnection } from "@/features/qlik-connections/api";
import { ConnectionFormFields } from "@/features/qlik-connections/components/connection-form";
import { ConnectionHelpAside } from "@/features/qlik-connections/components/connection-help-aside";
import { getErrorMessage } from "@/lib/api-client";

export const Route = createFileRoute("/_authenticated/connections_/new")({
  staticData: { pageTitle: "breadcrumbs.newConnection", breadcrumb: "breadcrumbs.newConnection" },
  component: NewConnectionPage,
});

function NewConnectionPage() {
  const navigate = useNavigate();
  const createMutation = useCreateConnection();
  const [authMethod, setAuthMethod] = useState<QlikAuthMethod>("api_key");
  const { t } = useTranslation("connections");

  const form = useForm<CreateQlikConnectionInput>({
    resolver: zodResolver(createQlikConnectionInputSchema),
    defaultValues: {
      authMethod: "api_key",
      name: "",
      baseUrl: "",
      apiKey: "",
    },
  });

  const handleMethodChange = (next: QlikAuthMethod) => {
    setAuthMethod(next);
    const common = {
      name: form.getValues("name") ?? "",
      baseUrl: form.getValues("baseUrl") ?? "",
    };
    form.reset(
      next === "oauth_client_credentials"
        ? { authMethod: "oauth_client_credentials", ...common, clientId: "", clientSecret: "" }
        : { authMethod: "api_key", ...common, apiKey: "" },
    );
  };

  const onSubmit = async (data: CreateQlikConnectionInput) => {
    try {
      await createMutation.mutateAsync(data);
      navigate({ to: "/connections", search: { q: undefined } });
    } catch {
      // Error surfaced via createMutation.error
    }
  };

  return (
    <PageShell
      header={
        <PageHeader
          title={t("new.pageTitle")}
          description={t("new.description")}
          back={
            <BackLink
              to="/connections"
              search={{ q: undefined }}
              label={t("new.backToConnections")}
            />
          }
        />
      }
    >
      <PageContent>
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-w-0">
            <ConnectionFormFields
              mode="create"
              formId="new-connection-form"
              authMethod={authMethod}
              onAuthMethodChange={handleMethodChange}
              register={form.register}
              errors={form.formState.errors}
              onSubmit={form.handleSubmit(onSubmit)}
              busy={createMutation.isPending}
              submitLabel={t("new.create")}
              submittingLabel={t("new.creating")}
              cancel={
                <Button
                  variant="outline"
                  onClick={() => navigate({ to: "/connections", search: { q: undefined } })}
                >
                  {t("form.cancel")}
                </Button>
              }
              submitError={createMutation.error ? getErrorMessage(createMutation.error) : undefined}
            />
          </div>
          <ConnectionHelpAside authMethod={authMethod} />
        </div>
      </PageContent>
    </PageShell>
  );
}
